import { ERROR_PREFIX } from './consts.js';
export const sanitizeXmlETag = (etag) => etag.replace(/^(&quot;|&#34;)+|(&quot;|&#34;)+$/g, '');
export const sanitizeETag = (etag) => etag?.replace(/^"+|"+$/g, '');
/**
 * Builds the `multipart/form-data` body of an S3 POST policy upload.
 * Rejects fields that would make S3 store elsewhere or redirect.
 */
export function buildPostPolicyForm(fields, data) {
    if (!fields.key?.trim() || /\$\{filename\}/.test(fields.key)) {
        throw new TypeError(`${ERROR_PREFIX}fields.key must be a concrete object key`);
    }
    // S3 matches form field names case-insensitively
    const names = Object.keys(fields).map((k) => k.toLowerCase());
    if (names.includes('file')) {
        throw new TypeError(`${ERROR_PREFIX}fields must not contain "file"`);
    }
    if (names.includes('success_action_redirect') || names.includes('redirect')) {
        throw new TypeError(`${ERROR_PREFIX}fields must use success_action_status, not success_action_redirect`);
    }
    const form = new FormData();
    for (const [k, v] of Object.entries(fields))
        form.set(k, v);
    // must be last: S3 ignores fields after `file`
    form.set('file', data);
    return form;
}
/** Object URL of a POST policy upload: the action URL is the bucket. */
export const postObjectLocation = (url, key) => `${removeQueryString(url).replace(/\/+$/, '')}/${uriResourceEscape(key)}`;
/** Drops any case of `content-type` from a header map. */
export function omitContentType(headers) {
    if (!headers)
        return headers;
    return Object.fromEntries(Object.entries(headers).filter(([k]) => k.toLowerCase() !== 'content-type'));
}
/** Strips query string and hash from a URL to derive the object location. */
export function removeQueryString(urlString) {
    const urlObject = new URL(urlString);
    urlObject.search = '';
    urlObject.hash = '';
    return urlObject.href;
}
const textEncoder = new TextEncoder();
const HEXS = '0123456789abcdef';
/**
 * Turn a raw ArrayBuffer into its hexadecimal representation.
 * @param {ArrayBuffer} buffer The raw bytes.
 * @returns {string} Hexadecimal string
 */
export const hexFromBuffer = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let hex = '';
    for (const byte of bytes) {
        hex += HEXS[byte >> 4] + HEXS[byte & 0x0f];
    }
    return hex;
};
/**
 * Compute SHA-256 hash of arbitrary string data.
 * @param {string} content The content to be hashed.
 * @returns {ArrayBuffer} The raw hash
 */
export const sha256 = async (content) => {
    const data = textEncoder.encode(content);
    return await globalThis.crypto.subtle.digest('SHA-256', data);
};
/**
 * Compute HMAC-SHA-256 of arbitrary data.
 * @param {string|ArrayBuffer} key The key used to sign the content.
 * @param {string} content The content to be signed.
 * @returns {ArrayBuffer} The raw signature
 */
export const hmac = async (key, content) => {
    const secret = await globalThis.crypto.subtle.importKey('raw', typeof key === 'string' ? textEncoder.encode(key) : key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const data = textEncoder.encode(content);
    return await globalThis.crypto.subtle.sign('HMAC', secret, data);
};
const entityMap = {
    '&quot;': '"',
    '&apos;': "'",
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
};
const unescapeXml = (value) => value.replaceAll(/&(quot|apos|lt|gt|amp);/g, (m) => entityMap[m] ?? m);
/**
 * Parse a very small subset of XML into a JS structure.
 *
 * @param input raw XML string
 * @returns string for leaf nodes, otherwise a map of children
 */
export const parseXml = (input) => {
    const xmlContent = input.replace(/<\?xml[^?]*\?>\s*/, '');
    const RE_TAG = /<([A-Za-z_][\w\-.]*)[^>]*>([\s\S]*?)<\/\1>/gm;
    const result = {}; // strong type, no `any`
    let match;
    // biome-ignore lint/suspicious/noAssignInExpressions: suppress
    while ((match = RE_TAG.exec(xmlContent)) !== null) {
        const tagName = match[1];
        const innerContent = match[2];
        const node = innerContent
            ? parseXml(innerContent)
            : unescapeXml(innerContent?.trim() || '');
        if (!tagName) {
            continue;
        }
        const current = result[tagName];
        if (current === undefined) {
            // First occurrence
            result[tagName] = node;
        }
        else if (Array.isArray(current)) {
            // Already an array
            current.push(node);
        }
        else {
            // Promote to array on the second occurrence
            result[tagName] = [current, node];
        }
    }
    // No child tags? — return the text, after entity decode
    return Object.keys(result).length > 0
        ? result
        : unescapeXml(xmlContent.trim());
};
/**
 * Encode a character as a URI percent-encoded hex value
 * @param c Character to encode
 * @returns Percent-encoded character
 */
const encodeAsHex = (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`;
/**
 * Escape a URI string using percent encoding
 * @param uriStr URI string to escape
 * @returns Escaped URI string
 */
const uriEscape = (uriStr) => {
    return encodeURIComponent(uriStr).replace(/[!'()*]/g, encodeAsHex);
};
/**
 * Escape a URI resource path while preserving forward slashes
 * @param string URI path to escape
 * @returns Escaped URI path
 */
export const uriResourceEscape = (string) => {
    return uriEscape(string).replaceAll('%2F', '/');
};
export class S3Error extends Error {
    code;
    constructor(msg, code, cause) {
        super(msg);
        this.name = new.target.name; // keeps instanceof usable
        this.code = code;
        this.cause = cause;
    }
}
export class S3NetworkError extends S3Error {
}
export class S3ServiceError extends S3Error {
    status;
    serviceCode;
    body;
    constructor(msg, status, serviceCode, body) {
        super(msg, serviceCode);
        this.status = status;
        this.serviceCode = serviceCode;
        this.body = body;
    }
}
